import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Close as CloseIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
} from '@mui/icons-material';
import axiosInstance from '../../../../utils/axios';
import modifyScriptName from '../../../../utils/modifyScriptName';

const TopGainersLosersModal = ({ open, onClose, userId, segment }) => {
  const [data, setData] = useState({ topGainers: [], topLosers: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);

  const formatValue = (value) => {
    if (value === undefined || value === null) return '0.00';
    return new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };
  const fetchTopGainersLosers = useCallback(async () => {
    if (!userId || !segment) return;
    
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.get(`/trade/top-gainers-losers/${userId}`, {
        params: {
          limit: 10,
          segment
        }
      });
      
      if (response.data.success) {
        setData(response.data.data);
      } else {
        setError('Failed to fetch data');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch top gainers and losers');
    } finally {
      setLoading(false);
    }
  }, [userId, segment]);
  useEffect(() => {
    if (open && userId && segment) {
      fetchTopGainersLosers();
    }
  }, [open, userId, segment, fetchTopGainersLosers]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };
  const renderTable = (dataArray, isGainer) => (
    <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
      <Table stickyHeader size="small">
        <TableHead>
          <TableRow>
            <TableCell align="left" sx={{ fontWeight: 600, border: '1px solid #e0e0e0' }}>Script Name</TableCell>
            <TableCell align="center" sx={{ fontWeight: 600, border: '1px solid #e0e0e0' }}>Gross P&L</TableCell>
            <TableCell align="center" sx={{ fontWeight: 600, border: '1px solid #e0e0e0' }}>Active P&L</TableCell>
            <TableCell align="center" sx={{ fontWeight: 600, border: '1px solid #e0e0e0' }}>Booked P&L</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {dataArray.length > 0 ? (
            dataArray.map((item, index) => (
              <TableRow key={item._id || index} hover>
                <TableCell align="left" sx={{ border: '1px solid #e0e0e0' }}>
                  <Box sx={{ display: 'flex', alignItems: 'left', gap: 1, justifyContent: 'left' }}>
                    {isGainer ? (
                      <TrendingUpIcon sx={{ color: 'success.main', fontSize: 16 }} />
                    ) : (
                      <TrendingDownIcon sx={{ color: 'error.main', fontSize: 16 }} />
                    )}
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {modifyScriptName(item.tradingSymbol)}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell align="center" sx={{ border: '1px solid #e0e0e0' }}>
                  <Chip
                    label={`₹${formatValue(item.grosspl)}`}
                    size="small"
                    color={item.grosspl >= 0 ? 'success' : 'error'}
                    sx={{ fontWeight: 600 }}
                  />
                </TableCell>
                <TableCell align="center" sx={{ border: '1px solid #e0e0e0' }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5,fontWeight:600 }}>
                    <Chip
                      label={`₹${formatValue(item.activepl)}`}
                      size="small"
                      color={item.activepl >= 0 ? 'success' : 'error'}
                      variant="outlined"
                    />
                    <Typography 
                      variant="caption" 
                      sx={{ color: (item.activeQuantity || 0) < 0 ? 'error.main' : 'success.main' }}
                    >
                      ({item.activeQuantity || 0})
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell align="center" sx={{ border: '1px solid #e0e0e0' }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5,fontWeight:600 }}>
                    <Chip
                      label={`₹${formatValue(item.bookpl)}`}
                      size="small"
                      color={item.bookpl >= 0 ? 'success' : 'error'}
                      variant="outlined"
                    />
                    <Typography variant="caption" color="text.secondary">
                      ({item.closedQuantity || 0})
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={4} align="center" sx={{ border: '1px solid #e0e0e0' }}>
                <Typography variant="body2" color="text.secondary">
                  No data available
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {segment} Top Gainers & Losers
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {!loading && !error && (
          <>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
            >
              <Tab
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TrendingUpIcon sx={{ fontSize: 16 }} />
                    Top Gainers ({data.topGainers?.length || 0})
                  </Box>
                }
              />
              <Tab
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TrendingDownIcon sx={{ fontSize: 16 }} />
                    Top Losers ({data.topLosers?.length || 0})
                  </Box>
                }
              />
            </Tabs>

            {activeTab === 0 && renderTable(data.topGainers || [], true)}
            {activeTab === 1 && renderTable(data.topLosers || [], false)}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

TopGainersLosersModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  userId: PropTypes.string,
  segment: PropTypes.oneOf(['NSE', 'MCX']),
};

export default TopGainersLosersModal;
